// Copyright (c) Jonathan Ferraz.
// Licensed under the MIT license.

import { RefObject } from 'react';

// Interface que define as propriedades aceitas pela função useScrollTo
interface ScrollProps {
  to: string | RefObject<HTMLElement>; // O destino da rolagem pode ser uma string de seletor CSS ou uma RefObject.
  parent?: string; // Opcional: Um seletor CSS do elemento pai para a rolagem (padrão é a janela do navegador).
  offset?: number; // Opcional: Um deslocamento (offset) em relação ao elemento de destino.
  duration?: number; // Opcional: A duração da animação de rolagem em milissegundos (padrão é 1000 ms).
}

// Função principal que cria um hook personalizado para rolagem suave.
export default function useScrollTo() {
  let currentAnimation: ReturnType<typeof setTimeout> | null = null; // Variável para controlar a animação de rolagem atual.

  // Função scrollTo que aceita as propriedades definidas na interface ScrollProps.
  const scrollTo = ({
    to,
    parent,
    offset,
    duration = 1000,
  }: ScrollProps): void => {
    let targetElement: HTMLElement | null;

    // Determina o elemento de destino com base no tipo de 'to' (string ou RefObject).
    if (typeof to === 'string') {
      targetElement = document.querySelector(to) as HTMLElement;
    } else {
      targetElement = to.current;
    }

    // Determina o elemento pai com base na propriedade 'parent'.
    const parentElement = parent
      ? (document.querySelector(parent) as HTMLElement)
      : window;

    // Verifica se os elementos de destino e pai são válidos.
    if (!targetElement || !parentElement) {
      console.error('Elemento de destino inválido ou elemento pai inválido');
      return;
    }

    // Calcula a posição final de rolagem levando em consideração o deslocamento.
    const target =
      offset! < 0
        ? targetElement.offsetTop - Math.abs(offset!)
        : !offset
        ? targetElement.offsetTop
        : targetElement.offsetTop + offset!;

    // Função que realiza a animação de rolagem suave.
    const smoothScrollTo = (
      endX: number,
      endY: number,
      duration?: number
    ): void => {
      const startX =
        parentElement instanceof Window
          ? parentElement.scrollX || parentElement.pageXOffset
          : parentElement.scrollLeft;
      const startY =
        parentElement instanceof Window
          ? parentElement.scrollY || parentElement.pageYOffset
          : parentElement.scrollTop;
      const distanceX = endX - startX;
      const distanceY = endY - startY;
      const startTime = new Date().getTime();

      duration = typeof duration !== 'undefined' ? duration : 1000;

      // Função de interpolação para suavizar a animação de rolagem.
      const easeInOutQuart = (
        time: number,
        from: number,
        distance: number,
        duration?: number
      ): number => {
        if ((time /= duration! / 2) < 1)
          return (distance / 2) * time * time * time * time + from;
        return (-distance / 2) * ((time -= 2) * time * time * time - 2) + from;
      };

      // Função que atualiza a posição de rolagem a cada frame.
      const animateScroll = () => {
        const time = new Date().getTime() - startTime;
        let newX = easeInOutQuart(time, startX, distanceX, duration);
        let newY = easeInOutQuart(time, startY, distanceY, duration);

        if (time >= duration!) {
          newX = endX;
          newY = endY;
          removeScrollLock();
        }

        // Atualiza a posição de rolagem com base no tipo de elemento pai.
        if (parentElement instanceof Window) {
          parentElement.scrollTo(newX, newY);
        } else {
          parentElement.scrollLeft = newX;
          parentElement.scrollTop = newY;
        }

        if (newX === endX && newY === endY) {
          removeScrollLock();
        } else {
          // Continua a animação se necessário.
          currentAnimation = setTimeout(animateScroll, 1000 / 60);
        }
      };

      // Função que impede a rolagem durante a animação.
      const scrollLock: EventListener = (event: Event): void => {
        event.preventDefault();
      };

      // Adiciona o bloqueio de rolagem.
      const addScrollLock = (): void => {
        parentElement.addEventListener('wheel', scrollLock, { passive: false });
      };

      // Remove o bloqueio de rolagem.
      const removeScrollLock = (): void => {
        parentElement.removeEventListener('wheel', scrollLock);
      };

      addScrollLock(); // Adiciona o bloqueio de rolagem.
      animateScroll(); // Inicia a animação de rolagem.
    };

    // Cancela a animação de rolagem atual se estiver próxima do fim.
    if (currentAnimation) {
      clearTimeout(currentAnimation);
    }

    smoothScrollTo(0, target, duration);
  };

  return scrollTo;
}
